import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import { usePreviewWithDataContext } from 'modules/templateEditor/context/PreviewWithDataContext';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { WebPushContentDefinition } from 'modules/graphql';
import { OSWebPush, TemplateEditViews } from 'modules/templateEditor/common';
import { IosPlaceholder } from 'ui/icons';

import { useWebPushPreviewContext } from '../context/WebPushPreviewContext';
import { getDate, getTimeSimple, truncateText } from '../../common/utils';
import {
  mobileDateStyle,
  mobileNotificationBubble,
  mobileNotificationContent,
  mobileNotificationContentHeader,
  mobileNotificationContentHeaderSender,
  mobileNotificationContentHeaderTime,
  mobileNotificationPlaceholder,
  mobileTimeStyle,
  mobileNotificationContentBody,
  mobileNotificationContentBodyMsg,
  mobileNotificationContentWrapper,
  mobileNotificationAttachmentWrapper,
  mobileNotificationAttachmentIos,
} from './styles';
import {
  WEB_PUSH_NON_WINDOWS_BODY_LIMIT,
  WEB_PUSH_NON_WINDOWS_TITLE_LIMIT,
  WEB_PUSH_WINDOWS_BODY_LIMIT,
  WEB_PUSH_WINDOWS_TITLE_LIMIT,
} from '../constants';

type Props = {
  selectedOS: OSWebPush;
};

export const MobilePreview = ({ selectedOS }: Props) => {
  const { liveTemplate } = useEditableFieldsContext();
  const { locale, view } = useQueryParamContext();
  // note: this date and time appear in a localized preview, so we specify the locale of the template
  const dateTime = new Date();
  const time = getTimeSimple(dateTime, locale);
  const date = getDate(dateTime, locale);

  const { messageContentTitle, messageContentBody } = useWebPushPreviewContext();
  const { isDataFeedDataEmpty, isUserDataEmpty } = usePreviewWithDataContext();
  const usePopulatedField =
    view === TemplateEditViews.preview && (!isDataFeedDataEmpty || !isUserDataEmpty);
  const webPushData = (liveTemplate?.mediumData || {}) as WebPushContentDefinition;
  const title = usePopulatedField ? messageContentTitle : webPushData.title;
  const body = usePopulatedField ? messageContentBody : webPushData.message;

  const supportsImage = selectedOS === OSWebPush.Android || selectedOS === OSWebPush.Windows;

  return (
    <>
      <div>
        <div style={mobileTimeStyle}>{time}</div>
        <div style={mobileDateStyle}>{date}</div>
      </div>
      <div style={mobileNotificationBubble} data-test="web-push-mobile-message">
        <div style={mobileNotificationPlaceholder}>
          <IosPlaceholder height={24} width={24} />
        </div>
        <div style={mobileNotificationContent}>
          <div style={mobileNotificationContentHeader}>
            <div style={mobileNotificationContentHeaderSender}>
              {truncateText(
                title,
                selectedOS === OSWebPush.Windows
                  ? WEB_PUSH_WINDOWS_TITLE_LIMIT
                  : WEB_PUSH_NON_WINDOWS_TITLE_LIMIT,
              )}
            </div>
            <div style={mobileNotificationContentHeaderTime}>now</div>
          </div>
          <div style={mobileNotificationContentWrapper}>
            <div style={mobileNotificationContentBody}>
              {/* SMS, Push, & WebPush do not support HTML, so do not use
               * dangerouslySetInnerHTML attr to display content (force text rendering)
               * Email & InApp DO support HTML and should be displayed with
               * dangerouslySetInnerHTML={{ __html: removeScript(content) }} so that HTML
               * renders and scripts are removed prior to rendering
               */}
              <div style={mobileNotificationContentBodyMsg}>
                {truncateText(
                  body,
                  selectedOS === OSWebPush.Windows
                    ? WEB_PUSH_WINDOWS_BODY_LIMIT
                    : WEB_PUSH_NON_WINDOWS_BODY_LIMIT,
                )}
              </div>
            </div>
            {webPushData.icon && supportsImage && (
              <div style={mobileNotificationAttachmentWrapper}>
                <img
                  style={mobileNotificationAttachmentIos}
                  src={webPushData.icon}
                  alt="Web Push Image"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
